import { combineReducers } from 'redux'
import { sha256 } from 'lib/createHash'
import {
  PAGE_VIEW,
  TRANSACTION_START,
  TRANSACTION_UPDATE,
  TRANSACTION_COMPLETE,
  TRANSACTION_FAILURE,
  TRANSACTION_EVENT,
  CART_EMPTY,
  CHECKOUT_START,
  CART_UPDATE_RESERVATION,
  CHECKOUT_UPDATE,
  API_CALL_SUCCESS,
  INITIALISE_SEARCH_SESSION_ID,
} from 'actions/actionConstants'
import { USER_LOGIN, USER_LOGOUT } from 'actions/apiActionConstants'
import { createReducer } from 'lib/reducerUtils'

export const initialTransactionState: App.AnalyticsTransactionState = {
  cartSuccess: false,
  orderSuccess: false,
  hasError: false,
  eventComplete: false,
  cart: {} as any,
  order: {} as App.Order,
  error: {},
  isNewOrder: false,
}

const initialCheckoutState: App.AnalyticsCheckoutState = {
  cart: {},
  checkoutStart: false,
  saveCard: false,
}

const initialPageState: App.AnalyticsPageState = {
  prevPathParams: {},
  prevPathTemplate: '',
  prevPath: '',
  currentPathParams: {},
  currentPathTemplate: '',
  currentPath: '',
}

export enum BuyerType {
    new = 'new',
    existing = 'existing'
}

const initialUserState: App.AnalyticsUserState = {
  userId: null,
  hashedEmail: null,
  signUpSource: null,
  attribution: {},
  userRoles: [],
  buyerType: BuyerType.new,
}

const initialSearchState: App.AnalyticsSearchState = {
  sessions: {},
}

function getBuyerType(data: any) {
  return data.number_of_purchases > 0 ? BuyerType.existing : BuyerType.new
}

export function transactionReducer(state = initialTransactionState, action): App.AnalyticsTransactionState {
  switch (action.type) {
    case TRANSACTION_START:
      return {
        ...state,
        cart: action.data,
        cartSuccess: true,
        eventComplete: false,
        isNewOrder: !action.data.order,
      }
    case TRANSACTION_UPDATE:
      return {
        ...state,
        cart: {
          ...state.cart,
          order: action.data.order,
        },
      }
    case TRANSACTION_COMPLETE:
      return {
        ...state,
        order: action.data,
        orderSuccess: true,
      }
    case TRANSACTION_FAILURE:
      return {
        ...state,
        error: action.data,
        hasError: true,
      }
    case TRANSACTION_EVENT:
      return {
        ...state,
        eventComplete: true,
      }
    case CART_EMPTY:
      return {
        ...state,
        cartSuccess: false,
        orderSuccess: false,
        eventComplete: false,
        hasError: false,
      }
    default:
      return state
  }
}

export function checkoutReducer(state = initialCheckoutState, action): App.AnalyticsCheckoutState {
  switch (action.type) {
    case CHECKOUT_START:
      return {
        ...state,
        cart: {
          ...state.cart,
          ...action.data,
        },
        checkoutStart: true,
      }
    case CART_UPDATE_RESERVATION:
      return {
        ...state,
        cart: {
          ...state.cart,
          ...action.data,
        },
      }
    case CHECKOUT_UPDATE:
      return {
        ...state,
        ...action.data,
      }
    case CART_EMPTY:
    case TRANSACTION_START:
      return {
        ...initialCheckoutState,
      }
    default:
      return state
  }
}

export function userReducer(state = initialUserState, action): App.AnalyticsUserState {
  switch (action.type) {
    case API_CALL_SUCCESS:
      if (action.api === USER_LOGIN) {
        return {
          ...state,
          userId: action.data.memberId,
          hashedEmail: sha256(action.data.email),
          signUpSource: action.data.signupDomain,
          userRoles: action.data.roles,
          // attribution is always empty now, feature no longer used
          attribution: {},
          buyerType: getBuyerType(action.data),
        }
      } else if (action.api === USER_LOGOUT) {
        return {
          ...initialUserState,
        }
      }
      if (action.api === USER_LOGOUT) {
        return {
          ...initialUserState,
        }
      }
      return state
    case CHECKOUT_START:
      return {
        ...state,
        attribution: {},
      }
    default:
      return state
  }
}

const searchReducer = createReducer<App.AnalyticsSearchState>(initialSearchState, {
  [INITIALISE_SEARCH_SESSION_ID]: (state, action) => {
    const { offerListKey, sessionId } = action

    return {
      ...state,
      sessions: {
        ...state.sessions,
        [offerListKey]: sessionId,
      },
    }
  },
})

const pageReducer = createReducer<App.AnalyticsPageState>(initialPageState, {
  [PAGE_VIEW]: (state, action) => {
    const { pathTemplate, pathParams, path } = action

    return {
      prevPath: state.currentPath,
      prevPathParams: state.currentPathParams,
      prevPathTemplate: state.currentPathTemplate,
      currentPath: path,
      currentPathParams: pathParams,
      currentPathTemplate: pathTemplate,
    }
  },
})

const analyticsReducer = combineReducers({
  transaction: transactionReducer,
  checkout: checkoutReducer,
  user: userReducer,
  search: searchReducer,
  page: pageReducer,
})

export default analyticsReducer
